import type { DomEventService, TimerService } from './services/browser';
import type { EventBus } from './EventBus';
import type { TrackingDebugService } from './services/TrackingDebugService';
import { TAB_ACTIVITY_DEBOUNCE_MS } from './config/tabVisibility';
import { TrackingBusEvent } from './constants/trackingBusEvent';
import { TrackingTag } from './constants/trackingTag';

/**
 * Translates browser lifecycle into bus events other modules subscribe to:
 *
 * - **Visibility:** `hidden` → `tab:inactive`; visible → `tab:active` plus an immediate `tab:activity` so
 *   `TabService` treats “back to tab” like interaction (refresh `last_active`). `HeartbeatLoop` schedules
 *   the next periodic collect from the same 30s cadence — no guaranteed immediate collect on resume.
 * - **Input:** debounced `tab:activity` on scroll/move/key/touch — debounce limits flood during long scrolls.
 * - **Unload:** `pagehide` → `page:unload` with `persisted` for bfcache; `pageshow` after restore → `page:bfcache`.
 *
 * Tab **blur** without visibility hidden does **not** fire here (by design): we only pause when the document
 * is actually hidden.
 */
export class TabVisibilityMonitor {
  private started = false;
  private unsubscribers: (() => void)[] = [];

  private activityDebounceId: number | null = null;
  private visible = document.visibilityState !== 'hidden';
  private unloadEmitted = false;

  constructor(
    private readonly events: DomEventService,
    private readonly timers: TimerService,
    private readonly bus: EventBus,
    private readonly debug?: TrackingDebugService,
    private readonly activityDebounceMs = TAB_ACTIVITY_DEBOUNCE_MS,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.debug?.log('TabVisibilityMonitor', 'start', {});

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.visible = false;
        this.debug?.log('TabVisibilityMonitor', 'visibility hidden', {});
        this.bus.emit(TrackingBusEvent.TabInactive, undefined, { publisher: TrackingTag.TabVisibilityMonitor });
      } else {
        this.visible = true;
        this.debug?.log('TabVisibilityMonitor', 'visibility visible', {});
        this.bus.emit(TrackingBusEvent.TabActive, undefined, { publisher: TrackingTag.TabVisibilityMonitor });
        this.bus.emit(TrackingBusEvent.TabActivity, undefined, { publisher: TrackingTag.TabVisibilityMonitor });
      }
    };

    const onActivity = () => {
      if (!this.visible) {
        return;
      }
      if (this.activityDebounceId != null) {
        return;
      }
      this.activityDebounceId = this.timers.setTimeout(() => {
        this.activityDebounceId = null;
        this.debug?.log('TabVisibilityMonitor', 'tab:activity (debounced)', { debounceMs: this.activityDebounceMs });
        this.bus.emit(TrackingBusEvent.TabActivity, undefined, { publisher: TrackingTag.TabVisibilityMonitor });
      }, this.activityDebounceMs);
    };

    this.events.on(document, 'visibilitychange', onVisibilityChange);
    this.unsubscribers.push(() => this.events.off(document, 'visibilitychange', onVisibilityChange));

    const activityEvents = ['scroll', 'mousemove', 'keypress', 'touchstart'] as const;
    for (const ev of activityEvents) {
      this.events.on(window, ev, onActivity, { passive: true });
      this.unsubscribers.push(() => this.events.off(window, ev, onActivity));
    }

    // Document is being unloaded (navigation away, tab close). Not fired on tab blur alone.
    // `persisted` is true when entering bfcache — async final collect may never run until restore.
    const onPageHide = (e: Event) => {
      const pe = e as PageTransitionEvent;
      const persisted = pe.persisted === true;
      if (this.unloadEmitted) {
        return;
      }
      this.unloadEmitted = true;
      this.debug?.log('TabVisibilityMonitor', 'pagehide → page:unload', { persisted });
      this.bus.emit(TrackingBusEvent.PageUnload, { persisted }, { publisher: TrackingTag.TabVisibilityMonitor });
    };
    this.events.on(window, 'pagehide', onPageHide);
    this.unsubscribers.push(() => this.events.off(window, 'pagehide', onPageHide));

    // Fallback for environments where `pagehide` is unreliable or logs are dropped at teardown.
    // No `persisted` signal here; treat as normal unload.
    const onBeforeUnload = () => {
      if (this.unloadEmitted) {
        return;
      }
      this.unloadEmitted = true;
      this.debug?.log('TabVisibilityMonitor', 'beforeunload → page:unload', { persisted: false });
      this.bus.emit(TrackingBusEvent.PageUnload, { persisted: false }, { publisher: TrackingTag.TabVisibilityMonitor });
    };
    this.events.on(window, 'beforeunload', onBeforeUnload);
    this.unsubscribers.push(() => this.events.off(window, 'beforeunload', onBeforeUnload));

    const onPageShow = (e: Event) => {
      const pe = e as PageTransitionEvent;
      if (!pe.persisted) {
        return;
      }
      this.debug?.log('TabVisibilityMonitor', 'pageshow restored from bfcache → page:bfcache', {});
      this.bus.emit(TrackingBusEvent.PageBfcache, undefined, { publisher: TrackingTag.TabVisibilityMonitor });
    };
    this.events.on(window, 'pageshow', onPageShow);
    this.unsubscribers.push(() => this.events.off(window, 'pageshow', onPageShow));
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    this.debug?.log('TabVisibilityMonitor', 'stop', {});

    if (this.activityDebounceId != null) {
      this.timers.clearTimeout(this.activityDebounceId);
      this.activityDebounceId = null;
    }
    this.unloadEmitted = false;

    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];
  }
}
